import { Link } from 'react-router-dom';
import { ProjectCard } from '@/features/projects';
import type { ProjectWithCustomer } from '@/features/projects';

interface RecentProjectsProps {
  projects: ProjectWithCustomer[];
  isLoading: boolean;
  maxDisplay?: number;
}

function ProjectCardSkeleton() {
  return (
    <div className="bg-white rounded-xl border border-secondary-200 p-5 animate-pulse">
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <div className="h-5 bg-secondary-200 rounded w-3/4 mb-2" />
          <div className="h-4 bg-secondary-200 rounded w-1/2" />
        </div>
        <div className="h-6 bg-secondary-200 rounded w-20" />
      </div>
      <div className="h-4 bg-secondary-200 rounded w-2/3" />
    </div>
  );
}

export function RecentProjects({
  projects,
  isLoading,
  maxDisplay = 6,
}: RecentProjectsProps) {
  const displayProjects = projects.slice(0, maxDisplay);
  const hasMore = projects.length > maxDisplay;

  return (
    <div className="space-y-4">
      {/* Section Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-secondary-900">Recent Projects</h2>
          <p className="text-sm text-secondary-500">Your latest renovation projects</p>
        </div>
        {projects.length > 0 && (
          <Link
            to="/projects"
            className="text-sm font-medium text-primary-600 hover:text-primary-700 
                       transition-colors flex items-center gap-1"
          >
            View all
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </Link>
        )}
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <ProjectCardSkeleton key={i} />
          ))}
        </div>
      )}

      {/* Empty State */}
      {!isLoading && projects.length === 0 && (
        <div className="text-center py-12 bg-white rounded-xl border border-secondary-200">
          <div className="w-16 h-16 bg-secondary-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-8 h-8 text-secondary-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
              />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-secondary-900">No Projects Yet</h3>
          <p className="text-secondary-600 mt-1 mb-6">
            Get started by creating your first project.
          </p>
          <Link
            to="/projects/create"
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary-600 
                       text-white font-medium rounded-lg hover:bg-primary-700 
                       transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
            Create First Project
          </Link>
        </div>
      )}

      {/* Projects Grid */}
      {!isLoading && projects.length > 0 && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {displayProjects.map((project) => (
              <ProjectCard key={project.id} project={project} />
            ))}
          </div>

          {/* Show more indicator */}
          {hasMore && (
            <div className="text-center pt-2">
              <Link
                to="/projects"
                className="text-sm text-secondary-500 hover:text-secondary-700 transition-colors"
              >
                +{projects.length - maxDisplay} more projects
              </Link>
            </div>
          )}
        </>
      )}
    </div>
  );
}